import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../state/auth-state';
import { getReturnUrl } from '../services/auth-client';
import { Spinner } from '../components/ui/spinner';
import { Alert, AlertDescription } from '../components/ui/alert';

/**
 * OAuth2 callback page for EntraID authorization code flow.
 * Exchanges the code for tokens and redirects to the saved return URL.
 */
export function CallbackPage(): React.JSX.Element {
  const { state, handleCallback } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const processedRef = React.useRef(false);

  React.useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;

    const code = searchParams.get('code');
    const error = searchParams.get('error');

    if (error) {
      navigate('/admin-console/login', { replace: true });
      return;
    }

    if (!code) {
      navigate('/admin-console/login', { replace: true });
      return;
    }

    handleCallback(code).then(() => {
      const returnUrl = getReturnUrl();
      navigate(returnUrl, { replace: true });
    }).catch((err) => {
      // Don't redirect — let the error state show on this page
      console.error('Auth callback failed:', err);
    });
  }, [searchParams, handleCallback, navigate]);

  if (state.error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md">
          <AlertDescription>{state.error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center" aria-busy="true">
      <Spinner label="Completing sign in..." />
    </div>
  );
}
